import { useState, useEffect, useRef } from 'react';
import butterflyApi from "../ButterflyApi";
import MobilePage from "../UiComponents/MobilePage"
import { Link, useParams, useSearchParams } from 'react-router-dom';
import Utils from "../utils";
import { isLogin } from '../Utils/Cookies';
import { StoryCover, StoryPage } from './StoryReaderPages';
import './StoryReader.css';

const mergeStoryAudio = (stories, audios) => {
  const audioByPageNo = new Map((audios || []).map(audio => [audio.pageNo, audio]))

  return (stories || []).map(story => {
    const audio = audioByPageNo.get(story.pageNo)

    return {
      ...story,
      audioUrl: audio?.audioUrl || null,
      audioGeneratedDatetime: audio?.generatedDatetime || null,
    }
  })
}


const FairytaleV2 = () => {

  const heroNo = isLogin() ? localStorage.getItem("heroNo") : 1
  const params = useParams()
  const [searchParams] = useSearchParams()
  const code = Utils.getFromCode(params.code)
  const scriptNo = code.scriptNo;
  const codeHeroNo = code.heroNo || 1;
  const sample = searchParams.get('sample') === 'true'

  const [script, setScript] = useState()
  const [stories, setStories] = useState([])
  const [storyHeroNo, setStoryHeroNo] = useState(null)
  const [isMyStory, setIsMyStory] = useState(false)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [page, setPage] = useState(0)
  const [queueInfo, setQueueInfo] = useState(null)
  const [playingPageNo, setPlayingPageNo] = useState(null)
  const [isAudioPaused, setIsAudioPaused] = useState(false)
  const audioRef = useRef(null)
  const audioPageNoRef = useRef(null)
  const audioAutoContinueRef = useRef(false)
  const audioFailedRef = useRef(false)
  const storiesRef = useRef([])

  const clearAudio = () => {
    if (!audioRef.current) return

    audioRef.current.onended = null
    audioRef.current.onerror = null
    audioRef.current.pause()
    audioRef.current.currentTime = 0
    audioRef.current = null
    audioPageNoRef.current = null
  }

  const stopAudio = () => {
    audioAutoContinueRef.current = false
    clearAudio()
    setPlayingPageNo(null)
    setIsAudioPaused(false)
  }

  const handleAudioError = () => {
    if (audioFailedRef.current) return

    audioFailedRef.current = true
    stopAudio()
    alert('오디오를 불러오지 못했습니다.')
  }

  const handleAudioEnded = (endedPageNo) => {
    if (audioRef.current) {
      audioRef.current.onended = null
      audioRef.current.onerror = null
    }

    audioRef.current = null
    audioPageNoRef.current = null
    setPlayingPageNo(null)
    setIsAudioPaused(false)

    if (!audioAutoContinueRef.current) return

    const currentIndex = storiesRef.current.findIndex(story => story.pageNo === endedPageNo)
    const nextStory = currentIndex >= 0 ? storiesRef.current[currentIndex + 1] : null

    if (!nextStory) {
      audioAutoContinueRef.current = false
      return
    }

    setPage(currentIndex + 2)

    if (nextStory.audioUrl) {
      setTimeout(() => playStoryAudio(nextStory, true), 0)
    } else {
      audioAutoContinueRef.current = false
    }
  }

  const playStoryAudio = async (story, autoContinue = true) => {
    if (!story?.audioUrl) return

    audioAutoContinueRef.current = autoContinue

    if (audioRef.current && audioPageNoRef.current === story.pageNo) {
      if (audioRef.current.paused) {
        try {
          await audioRef.current.play()
          setPlayingPageNo(story.pageNo)
          setIsAudioPaused(false)
        } catch (error) {
          handleAudioError()
        }
      } else {
        audioRef.current.pause()
        setPlayingPageNo(story.pageNo)
        setIsAudioPaused(true)
      }
      return
    }

    clearAudio()
    audioFailedRef.current = false

    const audio = new Audio(story.audioUrl)
    audioRef.current = audio
    audioPageNoRef.current = story.pageNo
    audio.onended = () => handleAudioEnded(story.pageNo)
    audio.onerror = handleAudioError

    try {
      await audio.play()
      setPlayingPageNo(story.pageNo)
      setIsAudioPaused(false)
    } catch (error) {
      handleAudioError()
    }
  }

  const onBefore = () => {
    stopAudio()
    setPage(current => Math.max(0, current - 1))
  }

  const onNext = () => {
    stopAudio()
    setPage(current => Math.min(stories.length, current + 1))
  }

  const load = async () => {
    const mergeAudioForHero = async (targetHeroNo, storyItems) => {
      const audioResponse = await butterflyApi.getHeroStoryAudios(targetHeroNo, scriptNo)
      return mergeStoryAudio(storyItems, audioResponse?.items || [])
    }

    const response = await butterflyApi.getHeroStory(codeHeroNo, scriptNo)
    let useDefaultStory = true

    // 스토리에 이미지까지 만들어진 경우에만
    // 해당 hero의 글/이미지를 사용한다.
    if (response?.items?.length > 0) {
      if (Number(heroNo) === Number(codeHeroNo) && response.items[0].imageUrl) { //
        setIsMyStory(true)
        const storiesWithAudio = await mergeAudioForHero(codeHeroNo, response.items)
        storiesRef.current = storiesWithAudio
        setStories(storiesWithAudio)
        setStoryHeroNo(storiesWithAudio[0]?.heroNo || codeHeroNo)
        useDefaultStory = false
      } else {
        // 내 동화가 아닌 경우
        setIsMyStory(false)
      }
    }

    // 이야기가 없거나(기존 로직), 혹은 이미지가 아직 없는 경우(default로 보여줌)
    if (useDefaultStory) {
      const defaultStoryResponse = await butterflyApi.getHeroStory(1, scriptNo)
      const defaultStoriesWithAudio = await mergeAudioForHero(1, defaultStoryResponse?.items || [])
      storiesRef.current = defaultStoriesWithAudio
      setStories(defaultStoriesWithAudio)
      setStoryHeroNo(defaultStoriesWithAudio[0]?.heroNo || 1)
      if (isLogin() && heroNo) {
        const queue = await butterflyApi.getImageTaskQueue(heroNo, scriptNo)
        setQueueInfo(queue)
      }
    }
    const script = await butterflyApi.getScriptInfo(codeHeroNo || 1, scriptNo)
    setScript(script)
  }

  useEffect(() => {
    load().catch(() => setLoadError('이야기를 불러오지 못했어요. 잠시 후 다시 찾아주세요.')).finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  }, [page])

  useEffect(() => {
    storiesRef.current = stories
  }, [stories])

  useEffect(() => {
    if (script && script.title) {
      document.title = `텔링파파 - ${script.title}`
    }
    return () => {
      // 컴포넌트 언마운트 시 기본 타이틀로 복원 (선택사항)
      document.title = '텔링파파'
    }
  }, [script])

  useEffect(() => {
    return () => {
      audioAutoContinueRef.current = false
      if (!audioRef.current) return

      audioRef.current.onended = null
      audioRef.current.onerror = null
      audioRef.current.pause()
      audioRef.current.currentTime = 0
    }
  }, [])

  const isSampleStory = sample || Utils.isSampleScript(script) || !isLogin() || (storyHeroNo && Number(storyHeroNo) !== Number(heroNo || 1))

  const currentStory = stories[page - 1]

  return <MobilePage className='storybook-page reader-page' topCheckDisabled={true}>
    {loading || loadError || !stories.length || !script ? <main className='reader-empty' aria-live='polite'>
      <img src='/image/main/books.png' alt='' width='64' height='64' />
      <h1 className='book-title'>{loading ? '이야기를 펼치고 있어요' : '잠시 쉬어가는 페이지'}</h1>
      <p className='book-description'>{loading ? '아이와 함께 읽을 동화를 준비하고 있어요.' : loadError || '아직 준비된 페이지가 없어요. 다른 이야기를 먼저 만나보세요.'}</p>
      {!loading && <Link className='book-secondary-button' to='/fairytales'>이야기 책장으로</Link>}
    </main> : page === 0 ?
      <StoryCover script={script} stories={stories} onNext={onNext} isMyStory={isMyStory} instagram={script?.instagram} queueInfo={queueInfo} isSampleStory={isSampleStory} />
      : currentStory && <StoryPage key={currentStory.pageNo} story={currentStory} title={script.title} pageNumber={page} totalPages={stories.length} onBefore={onBefore} onNext={onNext} instagram={script?.instagram} isLastPage={page === stories.length} isMyStory={isMyStory} onAudioToggle={playStoryAudio} isAudioPlaying={playingPageNo === currentStory.pageNo && !isAudioPaused} isAudioPaused={playingPageNo === currentStory.pageNo && isAudioPaused} onStopAudio={stopAudio} scriptNo={scriptNo} isSampleStory={isSampleStory} onSave={async (story, content) => {
          await butterflyApi.updateStoryContent(codeHeroNo, scriptNo, story.pageNo, content)
          await load()
        }} onImageRequest={async (story, content) => {
          await butterflyApi.requestImageChange(codeHeroNo, scriptNo, story.pageNo, content)
        }} />
    }

  </MobilePage>

}

export default FairytaleV2
